import { Modal } from '../components/Modal';

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
// Removed: import { Switch } from '../components/Switch'; // Clean up if unused
import { getClinicConfig, getPublicAvailability, createAppointmentsBatch } from '../services/storageService';
import { generateTimeSlots, formatDateBr } from '../utils/dateUtils';
import { maskPhone } from '../utils/maskUtils';
import { ClinicConfig, Appointment, Attendance } from '../types';
import { attendanceLabel, getAvailableAttendance, todaySP } from '../utils/schedulingRules';

interface SelectedSlot {
  date: string;
  time: string;
  attendance: Attendance;
}

interface PublicBookingProps {
  isEmbed?: boolean;
}

const localDate = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const EmbedWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen bg-transparent md:p-4 font-sans text-primary">{children}</div>
);

export const PublicBooking: React.FC<PublicBookingProps> = ({ isEmbed = false }) => {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [config, setConfig] = useState<ClinicConfig | null>(null);
  const [appointments, setAppointments] = useState<Appointment[]>([]);

  // Multi-select state
  const [selectedSlots, setSelectedSlots] = useState<SelectedSlot[]>([]);
  const [viewDate, setViewDate] = useState<string | null>(null);
  const [selectedAttendance, setSelectedAttendance] = useState<Attendance | null>(null);
  const [calendarView, setCalendarView] = useState<'month' | 'week' | 'day'>('month');

  // Mobile Modal State
  const [isSlotModalOpen, setIsSlotModalOpen] = useState(false);
  const [isSelectionListOpen, setIsSelectionListOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  // Form State
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientNotes, setClientNotes] = useState('');

  // Success State
  const [generatedAppointments, setGeneratedAppointments] = useState<{ date: string, time: string, code: string, attendance?: Attendance }[]>([]);

  // Calendar State
  const [calendarDate, setCalendarDate] = useState(new Date());

  // Error Modal State
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorDetails, setErrorDetails] = useState<{ date: string, time: string, error: string }[]>([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const [cfg, appts] = await Promise.all([
        getClinicConfig(),
        getPublicAvailability()
      ]);
      setConfig(cfg);
      setAppointments(appts);
    } catch (error) {
      console.error("Error loading data", error);
      setLoadError('Não foi possível carregar a agenda. Verifique sua conexão e tente novamente.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const availableSlotsForViewDate = useMemo(() => {
    if (!config || !viewDate) return [];
    return generateTimeSlots(viewDate, config, appointments);
  }, [viewDate, config, appointments]);

  const handlePrevMonth = () => {
    const newDate = new Date(calendarDate);
    if (calendarView === 'month') { newDate.setDate(1); newDate.setMonth(newDate.getMonth() - 1); }
    else newDate.setDate(newDate.getDate() - (calendarView === 'week' ? 7 : 1));
    setCalendarDate(newDate);
  };

  const handleNextMonth = () => {
    const newDate = new Date(calendarDate);
    if (calendarView === 'month') { newDate.setDate(1); newDate.setMonth(newDate.getMonth() + 1); }
    else newDate.setDate(newDate.getDate() + (calendarView === 'week' ? 7 : 1));
    setCalendarDate(newDate);
  };

  const calendarDays = useMemo(() => {
    if (calendarView !== 'month') {
      const start = new Date(calendarDate);
      if (calendarView === 'week') start.setDate(start.getDate() - start.getDay());
      return Array.from({ length: calendarView === 'week' ? 7 : 1 }, (_, index) => {
        const date = new Date(start); date.setDate(date.getDate() + index); return date;
      });
    }
    const year = calendarDate.getFullYear();
    const month = calendarDate.getMonth();
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);

    const daysInMonth = lastDayOfMonth.getDate();
    const startingDayOfWeek = firstDayOfMonth.getDay();

    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  }, [calendarDate, calendarView]);

  const isDateSelectable = (date: Date) => {
    if (!config) return false;
    const dateStr = localDate(date);
    if (dateStr <= todaySP()) return false;
    const slots = generateTimeSlots(dateStr, config, appointments);
    return slots.length > 0;
  };

  const toggleSlot = (date: string, time: string) => {
    const exists = selectedSlots.some(s => s.date === date && s.time === time);

    if (exists) {
      setSelectedSlots(prev => prev.filter(s => !(s.date === date && s.time === time)));
    } else {
      if (!selectedAttendance) return;
      const alreadySelectedOnDate = selectedSlots.find(s => s.date === date);

      if (alreadySelectedOnDate) {
        alert("Você só pode agendar um horário por dia. Se desejar trocar, desmarque o anterior.");
        return;
      }

      setSelectedSlots(prev => [...prev, { date, time, attendance: selectedAttendance }].sort((a, b) => {
        if (a.date !== b.date) return a.date.localeCompare(b.date);
        return a.time.localeCompare(b.time);
      }));
    }
  };

  const isSlotSelected = (date: string, time: string) => {
    return selectedSlots.some(s => s.date === date && s.time === time);
  };

  const dateHasSelection = (dateStr: string) => {
    return selectedSlots.some(s => s.date === dateStr);
  };

  const handleDateClick = (dateStr: string) => {
    setViewDate(dateStr);
    setSelectedAttendance(null);
    setIsSlotModalOpen(true);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setClientPhone(maskPhone(e.target.value));
  };

  const handleConfirm = async () => {
    if (!clientName || !clientPhone || selectedSlots.length === 0) return;
    if (submitting) return;
    if (clientName.trim().length < 3 || !/^\d{10,11}$/.test(clientPhone.replace(/\D/g, ''))) {
      setFormError('Informe seu nome e um telefone válido com DDD.');
      return;
    }
    setSubmitting(true);
    setFormError('');

    try {
      const apptsToCreate = selectedSlots.map(slot => ({
        clientName: clientName.trim(),
        clientPhone: clientPhone.replace(/\D/g, ''),
        date: slot.date,
        time: slot.time,
        attendance: slot.attendance,
        notes: clientNotes
      }));

      const result = await createAppointmentsBatch(apptsToCreate);

      // Partial Success Check
      if (result.errors && result.errors.length > 0) {
        setErrorDetails(result.errors);
        setIsErrorModalOpen(true);
      }

      if (result.created && result.created.length > 0) {
        setGeneratedAppointments(result.created);
        setStep(3); // Avança pra sucesso APENAS se algo foi agendado de fato
      }

    } catch (e: any) {
      // Hard failure (ex: nenhum horário disponível, erro 500)
      if (e.details && Array.isArray(e.details) && e.details.length > 0) {
        setErrorDetails(e.details);
        setIsErrorModalOpen(true);
      } else {
        const msg = (e as Error).message;
        setFormError(`Erro ao agendar: ${msg}`);
      }
      loadData();
    } finally {
      setSubmitting(false);
    }
  };

  const handleNewAppointment = async () => {
    await loadData();
    setClientName('');
    setClientPhone('');
    setClientNotes('');
    setSelectedSlots([]);
    setViewDate(null);
    setGeneratedAppointments([]);
    setStep(1);
    setIsSlotModalOpen(false);
  };

  const Wrapper = isEmbed ? EmbedWrapper : Layout;

  if (loadError) return <Wrapper><div className="max-w-lg mx-auto p-6 bg-white rounded-xl text-center" role="alert"><p className="mb-4 text-red-700">{loadError}</p><Button onClick={loadData}>Tentar novamente</Button></div></Wrapper>;

  if (loading || !config) return (
    <Wrapper>
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent"></div>
      </div>
    </Wrapper>
  );

  return (
    <Wrapper>
      <div className={`w-full max-w-4xl mx-auto ${isEmbed ? 'pb-4' : 'pb-20 md:pb-0'}`}>

        {/* Progress Bar */}
        <div className="flex items-center justify-center mb-6">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm transition-colors duration-300 ${step >= 1 ? 'bg-primary text-white' : 'bg-primary/20 text-primary'} font-bold shadow-sm`}>1</div>
          <div className={`w-12 md:w-16 h-1 transition-colors duration-300 ${step >= 2 ? 'bg-primary' : 'bg-primary/20'}`}></div>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm transition-colors duration-300 ${step >= 2 ? 'bg-primary text-white' : 'bg-primary/20 text-primary'} font-bold shadow-sm`}>2</div>
          <div className={`w-12 md:w-16 h-1 transition-colors duration-300 ${step >= 3 ? 'bg-primary' : 'bg-primary/20'}`}></div>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm transition-colors duration-300 ${step >= 3 ? 'bg-primary text-white' : 'bg-primary/20 text-primary'} font-bold shadow-sm`}>3</div>
        </div>

        {step === 1 && (
          <div className="flex flex-col gap-6 animate-fade-in">

            {/* Banner */}
            <div className="bg-blue-50 border-l-4 border-blue-400 p-3 md:p-4 rounded shadow-sm text-sm">
              <div className="flex">
                <div className="flex-shrink-0">
                  <span className="text-blue-400 text-lg">ⓘ</span>
                </div>
                <div className="ml-3">
                  <p className="text-blue-700">
                    Toque em um dia para ver os horários. Selecione múltiplos dias se desejar.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
              {/* LEFT: Calendar */}
              <div className="bg-white rounded-xl shadow-lg p-4 md:p-6 border border-primary/5">
                <div className="flex gap-2 mb-4" aria-label="Visualização da agenda">
                  {(['day', 'week', 'month'] as const).map(view => <button key={view} type="button" aria-pressed={calendarView === view} onClick={() => setCalendarView(view)} className={`flex-1 rounded-lg px-3 py-2 text-sm ${calendarView === view ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`}>{view === 'day' ? 'Dia' : view === 'week' ? 'Semana' : 'Mês'}</button>)}
                </div>
                <div className="flex justify-between items-center mb-6">
                  <button aria-label="Período anterior" onClick={handlePrevMonth} className="text-primary hover:bg-primary/10 p-2 rounded-full w-10 h-10 flex items-center justify-center">←</button>
                  <h3 className="text-lg md:text-xl font-serif text-primary font-bold capitalize">
                    {calendarDate.toLocaleDateString('pt-BR', { ...(calendarView === 'day' ? { day: 'numeric' as const } : {}), month: 'long', year: 'numeric' })}
                  </h3>
                  <button aria-label="Próximo período" onClick={handleNextMonth} className="text-primary hover:bg-primary/10 p-2 rounded-full w-10 h-10 flex items-center justify-center">→</button>
                </div>

                {calendarView === 'month' && <div className="grid grid-cols-7 gap-1 text-center mb-2">
                  {['D', 'S', 'T', 'Q', 'Q', 'S', 'S'].map((d, i) => (
                    <span key={i} className="text-xs font-bold text-gray-400 h-8 flex items-center justify-center">{d}</span>
                  ))}
                </div>}

                <div className={calendarView === 'month' ? 'grid grid-cols-7 gap-1 md:gap-2' : 'grid gap-2'}>
                  {calendarDays.map((date, idx) => {
                    if (!date) return <div key={idx} />;

                    const dateStr = localDate(date);
                    const selectable = isDateSelectable(date);
                    // Use dateHasSelection or simply check if dateStr is in selectedSlots
                    const isSelectedDay = dateHasSelection(dateStr);
                    // viewDate logic is less relevant for highlighting here since we use modal,
                    // but we can still highlight if it's the "active" date in modal context if we want.
                    // For now let's highlight if it has selected slots.

                    return (
                      <button
                        key={dateStr}
                        disabled={!selectable}
                        onClick={() => handleDateClick(dateStr)}
                        className={calendarView !== 'month' ? `w-full rounded-lg border p-3 flex justify-between text-sm ${isSelectedDay ? 'bg-primary text-white' : selectable ? 'text-primary hover:bg-primary/5' : 'text-gray-400 bg-gray-50'}` : `
                                        h-10 w-10 md:h-12 md:w-12 rounded-full flex items-center justify-center text-sm font-medium transition-all relative mx-auto
                                        ${isSelectedDay ? 'bg-primary text-white shadow-md' : ''}
                                        ${!isSelectedDay && selectable ? 'text-primary hover:bg-primary/10 hover:text-primary cursor-pointer' : ''}
                                        ${!selectable ? 'text-gray-300 cursor-not-allowed decoration-slice' : ''}
                                    `}
                      >
                        {calendarView === 'month' ? date.getDate() : <><span>{date.toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit' })}</span><span>{selectable ? `${generateTimeSlots(dateStr, config, appointments).length} horários` : 'Indisponível'}</span></>}
                        {isSelectedDay && (
                          <span className="absolute -bottom-1 w-1 h-1 bg-accent rounded-full"></span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Desktop Legend / Mobile Info */}
                <div className="mt-4 flex flex-wrap items-center justify-center gap-3 text-[10px] text-gray-400">
                  <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-primary"></div>Selecionado</div>
                  <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-gray-200"></div>Indisponível</div>
                </div>
              </div>

              {/* RIGHT: Selected List (Desktop largely) */}
              <div className="hidden md:block bg-white rounded-xl shadow-lg p-6 border border-primary/5 min-h-[300px]">
                <h3 className="font-bold text-lg text-primary mb-4 border-b pb-2">Resumo de Agendamento</h3>
                {selectedSlots.length === 0 ? (
                  <p className="text-gray-400 text-sm italic">Nenhum horário selecionado ainda.</p>
                ) : (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                    {selectedSlots.map((slot, idx) => (
                      <div key={`${slot.date}-${slot.time}`} className="flex justify-between items-center text-sm bg-gray-50 p-3 rounded border border-gray-100">
                        <span>{formatDateBr(slot.date)} às <strong>{slot.time}</strong><span className="block text-xs mt-1">{attendanceLabel(slot.attendance)}</span></span>
                        <button
                          aria-label={`Remover ${formatDateBr(slot.date)} às ${slot.time}`} onClick={() => toggleSlot(slot.date, slot.time)}
                          className="text-red-400 hover:text-red-600 px-2"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-8">
                  <Button
                    variant="primary"
                    disabled={selectedSlots.length === 0}
                    onClick={() => setStep(2)}
                    className="w-full h-12 text-lg shadow-lg"
                  >
                    Continuar com {selectedSlots.length} horário(s)
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* STEP 2 & 3 content (simplified for brevity, keeping existing logic mostly) */}
        {step === 2 && (
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-lg mx-auto border border-primary/5 animate-fade-in mb-8">
            <h2 className="text-2xl font-serif text-primary mb-6 text-center">Seus Dados</h2>
            {/* List of selected slots summary */}
            <div className="bg-gray-50 p-4 rounded-md mb-6 border border-gray-200/50">
              <div className="flex justify-between items-center mb-2">
                <p className="text-sm text-primary/70 font-bold">Resumo:</p>
                <button onClick={() => setStep(1)} className="text-xs text-primary underline">Editar</button>
              </div>
              <div className="max-h-32 overflow-y-auto">
                {selectedSlots.map((slot, idx) => (
                  <div key={idx} className="flex justify-between text-xs text-gray-600 py-1 border-b border-gray-200 last:border-0">
                    <span>{formatDateBr(slot.date)}</span>
                    <span>{slot.time} · {attendanceLabel(slot.attendance)}</span>
                  </div>
                ))}
              </div>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); handleConfirm(); }}>
              <Input label="Nome Completo" value={clientName} onChange={(e) => setClientName(e.target.value)} required placeholder="Ex: Maria Silva" />
              <Input label="Telefone (WhatsApp)" value={clientPhone} onChange={handlePhoneChange} required placeholder="(11) 99999-9999" type="tel" maxLength={15} />
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Observações (Opcional)</label>
                <textarea className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary text-sm" rows={3} value={clientNotes} onChange={(e) => setClientNotes(e.target.value)} />
              </div>
              <div className="flex gap-4 mt-8">
                <Button type="button" variant="secondary" disabled={submitting} onClick={() => setStep(1)} className="flex-1">Voltar</Button>
                <Button type="submit" variant="accent" disabled={submitting} className="flex-1">{submitting ? 'Enviando...' : 'Solicitar agendamento'}</Button>
              </div>
              {formError && <p role="alert" className="mt-4 text-sm text-red-700">{formError}</p>}
            </form>
          </div>
        )}

        {step === 3 && (
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-lg mx-auto text-center border-t-8 border-primary animate-fade-in">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"><span className="text-green-600 text-4xl">✓</span></div>
            <h2 className="text-3xl font-serif text-primary mb-2 font-bold">Solicitação recebida!</h2>
            <p className="text-gray-500 mb-8">Obrigado, {clientName}. Seus {generatedAppointments.length} horários estão aguardando confirmação da profissional. Guarde os códigos abaixo para acompanhar.</p>
            <div className="space-y-3 mb-8 max-h-60 overflow-y-auto">
              {generatedAppointments.map((appt, idx) => (
                <div key={idx} className="bg-gray-50 rounded-lg p-4 border border-dashed border-gray-300 text-left">
                  <div className="flex justify-between font-bold text-primary mb-1"><span>{formatDateBr(appt.date)}</span><span>{appt.time}</span></div>
                  <div className="text-xs text-gray-500">Cód: {appt.code}</div>
                  <p className="text-sm mt-2">{attendanceLabel(appt.attendance)}</p>
                  <p className="text-xs text-gray-600 mt-1">{appt.attendance === 'presencial' ? (config.address || 'Endereço a confirmar com a profissional.') : config.onlineInstructions}</p>
                </div>
              ))}
            </div>
            <div className="space-y-3">
              <a href={isEmbed ? '#/embed/tracking' : '#/tracking'} className="block w-full py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-hover shadow-md">Acompanhar</a>
              <Button variant="secondary" onClick={handleNewAppointment} className="w-full">Voltar</Button>
            </div>
          </div>
        )}

      </div>

      {/* --- ERROR MODAL (DUPLICATE/UNAVAILABLE) --- */}
      {isErrorModalOpen && (
        <Modal title="Atenção" onClose={() => setIsErrorModalOpen(false)} footer={<>
              <Button variant="secondary" onClick={() => setIsErrorModalOpen(false)} className="w-full border-red-200 text-red-700 hover:bg-red-50">
                Entendi
              </Button>
            </>}>
            <div className="space-y-3">
              <p className="text-gray-700 text-sm mb-4">
                Alguns horários não puderam ser agendados:
              </p>
              <div className="bg-red-50 rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto border border-red-100 mb-4">
                {errorDetails.map((err, idx) => (
                  <div key={idx} className="text-xs text-red-700 border-b border-red-200 last:border-0 pb-1 last:pb-0">
                    <p className="font-bold">{formatDateBr(err.date)} às {err.time}</p>
                    <p>{err.error}</p>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 text-center">
                Por favor, verifique seus agendamentos ou escolha outro horário.
              </p>
            </div>


        </Modal>
      )}

      {/* --- MODAL FOR TIME SLOTS (MOBILE/DESKTOP) --- */}
      {/*
          We use this modal for day selection now.
          When a date is clicked, this opens.
      */}
      {isSlotModalOpen && viewDate && (
        <Modal title="Selecionar atendimento e horário" subtitle={formatDateBr(viewDate)} onClose={() => setIsSlotModalOpen(false)} footer={<>
              <span className="text-xs text-gray-500">
                {availableSlotsForViewDate.length > 0 ? 'Toque para selecionar' : 'Escolha outro dia'}
              </span>
              <Button variant="primary" onClick={() => setIsSlotModalOpen(false)} className="text-sm px-6">
                Concluir
              </Button>
            </>}>
            <div className="space-y-3">
              <p className="text-sm font-medium text-primary mb-3">Como deseja ser atendido?</p>
              <div className="flex gap-2 mb-4">
                {getAvailableAttendance(viewDate, config).map(attendance => <button key={attendance} type="button" aria-pressed={selectedAttendance === attendance} onClick={() => { setSelectedAttendance(attendance); setSelectedSlots(previous => previous.map(slot => slot.date === viewDate ? { ...slot, attendance } : slot)); }} className={`flex-1 rounded-lg border p-3 text-sm font-medium ${selectedAttendance === attendance ? 'bg-primary text-white border-primary' : 'border-gray-200 text-primary hover:bg-primary/5'}`}>{attendanceLabel(attendance)}</button>)}
              </div>
              {selectedAttendance && <p className="text-xs text-gray-500 mb-4">{selectedAttendance === 'presencial' ? (config.address || 'Endereço a confirmar com a profissional.') : config.onlineInstructions}</p>}
              {!selectedAttendance ? <p className="text-sm text-gray-500 py-4">Selecione o atendimento para ver os horários.</p> : availableSlotsForViewDate.length === 0 ? (
                <div className="py-8 text-center text-gray-400">
                  <p>Nenhum horário disponível.</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-3">
                  {availableSlotsForViewDate.map(slot => {
                    const selected = isSlotSelected(viewDate, slot.time);
                    return (
                      <button
                        key={slot.time}
                        onClick={() => toggleSlot(viewDate, slot.time)}
                        className={`
                                            py-2 px-1 rounded-lg text-sm font-medium border transition-all
                                            ${selected
                            ? 'bg-accent text-white border-accent shadow-md'
                            : 'bg-white text-primary border-gray-200 hover:border-accent hover:text-accent'}
                                        `}
                      >
                        {slot.time}
                        {selected && <span className="ml-1 text-[10px]">✓</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer of Modal */}

        </Modal>
      )}

      {/* --- SELECTED SLOTS REVIEW MODAL (MOBILE) --- */}
      {isSelectionListOpen && (
        <Modal title="Seus horários" onClose={() => setIsSelectionListOpen(false)} footer={<>
              <Button variant="primary" onClick={() => setIsSelectionListOpen(false)} className="w-full">
                Fechar
              </Button>
            </>}>
            <div className="space-y-3">
              {selectedSlots.length === 0 ? (
                <p className="text-gray-400 text-center py-4">Nenhum horário selecionado.</p>
              ) : (
                <div className="space-y-2">
                  {selectedSlots.map((slot, idx) => (
                    <div key={`${slot.date}-${slot.time}`} className="flex justify-between items-center text-sm bg-gray-50 p-3 rounded border border-gray-100">
                      <span>{formatDateBr(slot.date)} às <strong className="text-primary">{slot.time}</strong><span className="block text-xs mt-1">{attendanceLabel(slot.attendance)}</span></span>
                      <button
                        aria-label={`Remover ${formatDateBr(slot.date)} às ${slot.time}`} onClick={() => toggleSlot(slot.date, slot.time)}
                        className="text-red-400 hover:text-red-600 px-2 font-bold"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>


        </Modal>
      )}

      {/* --- STICKY FOOTER (MOBILE ONLY - When items selected & Step 1) --- */}
      {step === 1 && selectedSlots.length > 0 && (
        <div className="fixed bottom-0 left-0 w-full bg-white border-t border-gray-200 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] p-4 md:hidden z-40 flex justify-between items-center animate-slide-up">
          <div onClick={() => setIsSelectionListOpen(true)} className="flex items-center gap-2 cursor-pointer group">
            <div className="bg-primary/10 p-2 rounded-full text-primary group-active:scale-95 transition-transform">
              <span className="font-bold text-lg">📝</span>
            </div>
            <div>
              <p className="text-sm font-bold text-primary">{selectedSlots.length} horário(s)</p>
              <p className="text-xs text-primary underline">Ver/Editar lista</p>
            </div>
          </div>
          <Button variant="accent" onClick={() => setStep(2)} className="shadow-lg shadow-orange-200 px-6">
            Continuar
          </Button>
        </div>
      )}

    </Wrapper>
  );
};
